import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Organization, useDeleteOrganization } from "@/hooks/useOrganizations";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

interface OrganizationsTableProps {
  organizations: Organization[];
  isLoading: boolean;
  onEdit: (organization: Organization) => void;
}

const OrganizationsTable = ({ organizations, isLoading, onEdit }: OrganizationsTableProps) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { mutate: deleteOrganization } = useDeleteOrganization();

  const handleDelete = () => {
    if (deleteId) {
      deleteOrganization(deleteId);
      setDeleteId(null);
    }
  };

  const getTypeLabel = (type: string) => {
    return type === "persona_fisica" ? "Persona Física" : "Persona Moral";
  };

  const getTypeVariant = (type: string): "default" | "secondary" => {
    return type === "persona_fisica" ? "default" : "secondary";
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Cargando organizaciones...</div>;
  }

  if (organizations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay organizaciones registradas
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Fecha de Creación</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations.map((organization) => (
              <TableRow key={organization.id}>
                <TableCell className="font-medium">{organization.name}</TableCell>
                <TableCell>
                  <Badge variant={getTypeVariant(organization.type)}>
                    {getTypeLabel(organization.type)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {format(new Date(organization.created_at), "dd 'de' MMMM, yyyy", { locale: es })}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(organization)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(organization.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la organización.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OrganizationsTable;
